/* eslint-disable no-loop-func */
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Button from "../../Generic/Button";
import Card from "../../Generic/Card";
import moment from "moment";
import CookBookConnector from "../Connector/CookBookConnector";
import { errorNotification, successNotification } from "../../Generic/Notifications";
import Loader from "../../Generic/Loader";
import EmptyState from "../../Generic/EmptyState";
import { useParams, useNavigate } from "react-router-dom";
import StudioService from "../Services/StudioService";
import useQuery from "../../util/hooks/useQuery";
import { getIngredientServingState, INGREDIENT_SERVING_STATE_NAME_MAP } from "../Utils/ingredientServingState";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1rem;
`;

const Header = styled.div`
	display: flex;
	justify-content: flex-start;
	font-size: 2.3rem;
	margin: ${(props) => props.margin};
	width: 100%;
	white-space: normal;
	overflow-wrap: break-word;
	word-wrap: break-word;
	word-break: break-word;
`;

const Div = styled.div`
	position: ${(props) => props.position};
	width: ${(props) => props.width};
	display: flex;
	flex-direction: ${(props) => props.flexDirection};
	flex-wrap: ${(props) => props.flexWrap};
	justify-content: ${(props) => props.jContent};
	align-items: ${(props) => props.alignItem};
	padding: ${(props) => props.padding};
	margin: ${(props) => props.margin};
	text-align: ${(props) => props.txtAlign};
	font-size: ${(props) => props.fontSize};
	font-family: ${(props) => props.fontFamily};
	gap: ${(props) => props.gap};
	border: ${(props) => props.border};
	border-radius: ${(props) => props.borderRadius};
	@media (max-width: 1000px) {
		justify-content: ${(props) => props.jContentBefore1000};
	}
`;

const Span = styled.span`
	font-size: ${(props) => props.fontSize};
	font-weight: ${(props) => props.fontWeight};
	font-family: ${(props) => props.fontFamily};
	color: ${(props) => props.color};
	margin: ${(props) => props.margin};
	padding: ${(props) => props.padding};
	text-align: ${(props) => props.textAlign};
`;

const H1 = styled.div`
	font-weight: 600;
	font-size: 24px;
`;

const H2 = styled.div`
	font-weight: 400;
	font-size: 18px;
`;

const H3 = styled.div`
	font-weight: 400;
	font-size: 14px;
	color: rgba(0, 0, 0, 0.5);
`;

const P1 = styled.div`
	font-weight: 400;
	font-size: 18px;
	color: rgba(0, 0, 0, 0.8);
`;

const Block = styled(Div)`
	flex-direction: column;
	gap: 0.5rem;
`;

const Seperator = styled.div`
	border: 1px solid rgba(0, 0, 0, 0.1);
	width: 100%;
`;

const Overlay = styled.div`
	position: fixed;
	display: flex;
	justify-content: center;
	align-items: center;
	background: rgba(0, 0, 0, 0.3);
	left: 0;
	top: 0;
	right: 0;
	bottom: 0;
	z-index: 310;
`;

const RecipeDetails = (props) => {
	const { Patterns } = props;

	const navigate = useNavigate();
	const pageQuery = useQuery();
	const initialLoading = pageQuery.get("action") === "noReload" ? false : true;
	const { id } = useParams();
	const [loading, setLoading] = useState(initialLoading);
	const [pageDisable, setPageDisable] = useState(false);
	const [error, setError] = useState(false);

	async function verifyRecipe(id) {
		const payload = {
			"verified" : true
		}

		setPageDisable(true);

		const { data, error } = await StudioService.updateRecipeVerificationStatus(id, payload)
		if(data) {
			successNotification("Updated Recipe Verification Status");
			await fetchRecipe();
			setPageDisable(false)
		}
		if(error) {
			errorNotification("Could not update Recipe Verification Status");
			setPageDisable(false)
		}
	}

	function renderSegments(segments) {
		let renderData = [];

		for (const segmentId in segments) {
			let ingredientsRenderData = [];
			const { ingredients, quantity, name } = segments[segmentId];

			for (const ingredientId in ingredients) {
				const { selectionType, quantity, pattern, servingTemp, amountPerPortion, position, name} = ingredients[ingredientId];
				const PatternDetails = Object.entries(Patterns).find(([key, item]) => {
					if (item == pattern?.id) return true;
					return false;
				});

				const servingState = getIngredientServingState(servingTemp);
				const servingStateName = INGREDIENT_SERVING_STATE_NAME_MAP[servingState];
				ingredientsRenderData.push(
					<Div flexDirection="column" padding="1rem" gap="1rem" border="1px solid rgba(0, 0, 0, 0.2)" borderRadius="10px">
						<Div>
							<H2>{name || ingredientId}</H2>
						</Div>
						<Div flexDirection="row" gap="1rem">
							<Block>
								<H3>Default</H3> {selectionType?.isDefault ? "Yes" : "No"}
							</Block>
							<Block>
								<H3>Mandatory</H3> {selectionType?.isMandatory ? "Yes" : "No"}
							</Block>
						</Div>
						<Div flexDirection="row" gap="1rem">
							<Block>
								<H3>Min Portions</H3> {quantity?.minPortion ?? "-"}
							</Block>
							<Block>
								<H3>Max Portions</H3> {quantity?.maxPortion ?? "-"}
							</Block>
							<Block>
								<H3>Default Portions</H3> {quantity?.defaultPortion ?? "-"}
							</Block>
						</Div>
						<Div flexDirection="row" gap="1rem">
							<Block>
								<H3>Pattern</H3> {PatternDetails?.[0] ?? "-"}
							</Block>
							<Block>
								<H3>Amount Per Portion</H3> {amountPerPortion ?? "-"}
							</Block>
							<Block>
								<H3>Position</H3> {position ?? "-"}
							</Block>
							<Block>
								<H3>Serving State</H3> {servingStateName ?? "-"}
							</Block>
						</Div>
					</Div>
				);
			}

			renderData.push(
				<Div flexDirection="column" padding="0" gap="1rem">
					<Div>
						<H1>{name}</H1>
					</Div>
					<Div flexDirection="row" gap="1rem" alignItem="flex-end">
						<Block>
							<H3>Min Portion</H3> <P1>{quantity?.minPortion ?? "-"}</P1>
						</Block>
						<Block>
							<H3>Max Portion</H3> <P1>{quantity?.maxPortion ?? "-"}</P1>
						</Block>
						<Block>
							<H3>Min Primary Selection</H3> <P1>{quantity?.minPrimarySelection ?? "-"}</P1>
						</Block>
					</Div>
					<Div>
						<Span fontWeight="600" margin="0 0 0 0" color="rgba(0,0,0,0.6)">
							Ingredients
						</Span>
					</Div>
					<Div gap="2rem" flexWrap="wrap">
						{ingredientsRenderData}
					</Div>
				</Div>
			);

			renderData.push(<Seperator />);
		}

		if (!renderData.length) {
			renderData.push(
				<Div alignItem="center" jContent="center">
					<Span color="rgba(0, 0, 0, 0.6)">No Segments Found</Span>
				</Div>
			);
		}

		return renderData;
	}

	function renderRecipe(recipe) {
		return (
			<Div width="100%" flexDirection="column">
				<Card gap="1.5rem" borderRadius="10px" padding="1.5rem" margin="0 0 0 0" width="100%" alignItem="center">
					<Div flexDirection="column" padding="0" width="100%">
						<Header>{recipe?.name ? recipe.name : ""}</Header>
						<Span color="rgba(0,0,0,0.5)" fontWeight="600" margin="0.5rem 0 0 0">
							{recipe?.description}
						</Span>
						<Span color="rgba(0,0,0,0.5)" fontWeight="600" margin="0.5rem 0 0 0">
							{recipe?.note}
						</Span>
					</Div>
					<Div gap="1.5rem" width="100%" alignItem="center" flexWrap="wrap">
						<Div flexDirection="column" padding="0">
							<Span color="rgba(0,0,0,0.6)">Preparation Time</Span>
							<Span fontWeight="600" margin="0.5rem 0 0 0">
								{recipe?.preparationTime ? recipe?.preparationTime + "s" : "\xa0 -"}
							</Span>
						</Div>
						<Div flexDirection="column" padding="0">
							<Span color="rgba(0,0,0,0.6)">Updated At</Span>
							<Span fontWeight="600" margin="0.5rem 0 0 0">
								{recipe?.updatedAt ? moment(recipe.updatedAt).format("MMMM Do YYYY hh:mm:ss A") : "\xa0 -"}
							</Span>
						</Div>
						<Div flexDirection="column" padding="0">
							<Span color="rgba(0,0,0,0.6)">Verified</Span>
							<Span fontWeight="600" margin="0.5rem 0 0 0">
								{recipe?.verified ? "Yes" : "No"}
							</Span>
						</Div>
						{!recipe.verified ? (
							<Div flexDirection="column" padding="0">
								<Button onClick={() => verifyRecipe(recipe._id)} >Verify Recipe</Button>
							</Div>
						) : (
							""
						)}
					</Div>
					<Div flexDirection="column" width="100%" gap="0.5rem">
						{/* <Span color="rgba(0,0,0,0.6)">Segments : </Span> */}
						<Div flexDirection="column" gap="2rem" width="100%">
							{renderSegments(recipe?.segments)}
						</Div>
					</Div>
				</Card>
			</Div>
		);
	}

	async function fetchRecipe() {
		if (pageQuery.get("action") === "noReload") return;

		setLoading(true);

		const response = await StudioService.getRecipe(id);
		const { data, error } = response;
		if (data) {
			props.getRecipe(data);
			setLoading(false);
			setError(false);
		}
		if (error) {
			setLoading(false);
			setError(true);
			errorNotification(error);
		}
	}

	useEffect(() => {
		fetchRecipe();
	}, []);

	return (
		<Page>
			<Div padding="0 0 1rem 0" width="100%" jContent="space-between" alignItem="center">
				<span
					onClick={() => {
						navigate("/studio");
					}}
					className="material-symbols-outlined"
					style={{ cursor: "pointer" }}>
					arrow_back
				</span>
				<Button
					onClick={() => {
						navigate(`/studio/recipe/${id}/edit`);
						props.changeEditMode({ objectToEdit: props.selectedRecipe });
					}}>
					Edit
				</Button>
			</Div>
			{!loading && !error && renderRecipe(props.selectedRecipe)}
			{loading && !error && <Loader />}
			{error && <EmptyState retry={fetchRecipe} />}
			{pageDisable ? <Overlay /> : ""}
		</Page>
	);
};

export default CookBookConnector(RecipeDetails);
